// --- Dependencies -------------------------------------------------------------------------------
import { useNavigate, useParams } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TySystemLogDetailResponse } from "@/types";
import ActionsBar from "@/UI/components/ActionsBar";
import { formatDateTime, formatDuration, SEVERITY_TONES } from "../LogsScreen/helpers";
import Loader from "./Loader";
import "./styles.css";

// --- Logic --------------------------------------------------------------------------------------

export default function OneLogScreen() {
    const t = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const lang = useLang((state) => state.lang) ?? "ar";

    const { data, loading, error, invoke } = useFetch<TyApiResponse<TySystemLogDetailResponse>>(`logs/${id}`, { method: "GET" });

    if (loading) return <Loader />;

    if (error || !data?.data) {
        return (
            <div className="one-log-empty">
                <p className="one-log-empty-title">{error ?? t.logs.detailNotFound}</p>
                <button type="button" className="one-log-retry" onClick={() => invoke()}>
                    {t.storeDetail.retry}
                </button>
            </div>
        );
    }

    const { log } = data.data;

    /** Payloads are JSON blobs, so they render LTR whatever the interface language is. */
    const payload = (title: string, value: unknown) => (
        <section className="one-log-section">
            <p className="one-log-section-title">{title}</p>
            {value === null || value === undefined ? (
                <p className="one-log-nopayload">{t.logs.detailNoPayload}</p>
            ) : (
                <pre className="one-log-pre" dir="ltr">
                    {typeof value === "string" ? value : JSON.stringify(value, null, 4)}
                </pre>
            )}
        </section>
    );

    const rows: [string, string | null][] = [
        [t.logs.detailAt, formatDateTime(log.createdAt, lang)],
        [t.logs.detailResponseTime, formatDuration(log.responseTime)],
        [t.logs.detailMessage, log.message],
        [t.logs.detailStore, log.store ? log.store.name || log.store.slug : null],
        [t.logs.detailBranch, log.branch ? log.branch.name || log.branch.slug : null],
        [t.logs.detailClient, log.client ? `${log.client.name} — ${log.client.phone}` : null],
        [t.logs.detailAdmin, log.admin ? `${log.admin.name} — ${log.admin.phone}` : null],
        [t.logs.detailLang, log.lang],
        [t.logs.detailIp, log.ip],
        [t.logs.detailUserAgent, log.userAgent],
    ];

    return (
        <div className="one-log-screen">
            <ActionsBar onBack={() => navigate("/logs")} title={t.logs.detailTitle} />

            <section className="one-log-head">
                <div className="one-log-head-title">
                    <span className="one-log-method" dir="ltr">
                        {log.method}
                    </span>
                    <span className="one-log-status" data-tone={SEVERITY_TONES[log.severity]} dir="ltr">
                        {log.status}
                    </span>
                    <span className="one-log-outcome">{t.enums.logOutcome[log.outcome]}</span>
                </div>
                <p className="one-log-url" dir="ltr">
                    {log.url}
                </p>
                {log.route && (
                    <p className="one-log-route" dir="ltr">
                        {log.route}
                    </p>
                )}
            </section>

            <section className="one-log-section">
                <p className="one-log-section-title">{t.logs.detailRequest}</p>
                <div className="one-log-rows">
                    {rows
                        .filter(([, value]) => value)
                        .map(([label, value]) => (
                            <div key={label} className="one-log-row">
                                <span>{label}</span>
                                <strong>{value}</strong>
                            </div>
                        ))}
                </div>
            </section>

            {log.truncated && <p className="one-log-truncated">{t.logs.detailTruncated}</p>}

            {payload(t.logs.detailQuery, log.query)}
            {payload(t.logs.detailBody, log.body)}
            {payload(t.logs.detailResponse, log.response)}
        </div>
    );
}
